import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  cartItemCount?: number;
}

export default function Navbar({ cartItemCount = 0 }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/76cde12d-0453-4a53-99ef-032f21010e21.png"
              alt="Store Logo"
              className="h-20 w-auto object-contain"
            />
          </Link>

          {/* Center Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/') 
                  ? 'text-black' 
                  : isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              Home
            </Link>
            <span className={`${isScrolled ? 'text-gray-300' : 'text-white/50'}`}>•</span>
            <Link
              to="/products"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/products') 
                  ? 'text-black' 
                  : isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              Products
            </Link>
            <span className={`${isScrolled ? 'text-gray-300' : 'text-white/50'}`}>•</span>
            <Link
              to="/#about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              About
            </Link>
            <span className={`${isScrolled ? 'text-gray-300' : 'text-white/50'}`}>•</span>
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isActive('/contact')
                  ? 'text-black'
                  : isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              Contact
            </Link>
            <span className={`${isScrolled ? 'text-gray-300' : 'text-white/50'}`}>•</span>
            <Link
              to="/track-order"
              className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                isActive('/track-order')
                  ? 'text-black'
                  : isScrolled ? 'text-gray-600 hover:text-black' : 'text-white hover:text-gray-200'
              }`}
            >
              <i className="ri-map-pin-time-line text-base"></i>
              Track Order
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            <Link
              to="/request-quote"
              className={`hidden lg:flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium border transition-all whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'border-black text-black hover:bg-black hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-black'
              }`}
            >
              <i className="ri-file-list-3-line"></i>
              Get a Quote
            </Link>
            <Link
              to="/cart"
              className="relative cursor-pointer"
            >
              <i className={`ri-shopping-cart-line text-2xl ${isScrolled ? 'text-black' : 'text-white'}`}></i>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartItemCount}
                </span>
              )}
            </Link>
            <button
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                isScrolled
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-white text-black hover:bg-gray-100'
              }`}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
